import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'

import type { WorkbookBucketConfig } from './workbook.types'
import {
    type BuilderFieldEntry,
    type CollectionState,
    applyBuilderFieldCollectionState,
    builderFieldsForBucketConfig,
    getBuilderFieldCollectionState,
} from './workbookFieldUtils'

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        tableLayout: 'fixed',
    },
    headCell: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.75, 1),
    },
    fieldCell: {
        padding: theme.spacing(0.75, 1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        verticalAlign: 'middle',
    },
    fieldLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        fontSize: '0.875rem',
    },
    collectionCell: {
        padding: theme.spacing(0.5, 1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        verticalAlign: 'middle',
        width: 280,
    },
    toggleGroup: {
        width: '100%',
        '& .MuiToggleButton-root': {
            flex: 1,
            padding: theme.spacing(0.5, 0.75),
            fontSize: '0.75rem',
            lineHeight: 1.2,
            textTransform: 'none',
        },
    },
    cautionIcon: {
        color: theme.palette.warning.main,
        fontSize: '1rem',
    },
}))

interface BucketFieldListProps {
    bucket: WorkbookBucketConfig
    readOnly?: boolean
    onChange: (bucket: WorkbookBucketConfig) => void
}

export const BucketFieldList: React.FC<BucketFieldListProps> = ({
    bucket,
    readOnly,
    onChange,
}) => {
    const classes = useStyles()
    const builderFields = builderFieldsForBucketConfig(bucket)

    const setFieldState = (builderField: BuilderFieldEntry, state: CollectionState) => {
        onChange(applyBuilderFieldCollectionState(bucket, builderField, state))
    }

    return (
        <Table size="small" className={classes.table}>
            <TableHead>
                <TableRow>
                    <TableCell className={classes.headCell}>Field</TableCell>
                    <TableCell className={classes.headCell} align="right">
                        Collection
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {builderFields.map((builderField) => {
                    const state = getBuilderFieldCollectionState(bucket, builderField)
                    return (
                        <TableRow key={builderField.id}>
                            <TableCell className={classes.fieldCell}>
                                <Typography className={classes.fieldLabel}>
                                    {builderField.label}
                                    {builderField.fha_tier === 'caution' && (
                                        <Tooltip title="Use with care under fair housing guidelines.">
                                            <WarningIcon
                                                className={classes.cautionIcon}
                                            />
                                        </Tooltip>
                                    )}
                                </Typography>
                            </TableCell>
                            <TableCell className={classes.collectionCell} align="right">
                                <ToggleButtonGroup
                                    className={classes.toggleGroup}
                                    size="small"
                                    exclusive
                                    value={state}
                                    onChange={(_, value: CollectionState | null) => {
                                        if (readOnly || !value) return
                                        setFieldState(builderField, value)
                                    }}
                                >
                                    <ToggleButton value="skip" disabled={readOnly}>
                                        Skip
                                    </ToggleButton>
                                    <ToggleButton value="optional" disabled={readOnly}>
                                        Optional
                                    </ToggleButton>
                                    <ToggleButton value="required" disabled={readOnly}>
                                        Required
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </TableCell>
                        </TableRow>
                    )
                })}
            </TableBody>
        </Table>
    )
}
